import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import productUsageService from '../../services/productUsageService';
import { buildMonthOptions, defaultMonth } from '../../utils/reportingPeriods';
import ProductUsageTable from './ProductUsageTable';
import CSGFReporting from './CSGFReporting';
import DataUploads from './DataUploads';

const TAB_ALL = 'all';
const TAB_CSGF = 'csgf';
const TAB_UPLOADS = 'uploads';

const TAB_COPY = {
  [TAB_ALL]: 'Per-tool usage across all users, for the current month, a rolling 90-day window, and all recorded time.',
  [TAB_CSGF]: 'Partnership Update metrics, product usage, and the supporting user, network, and school records for the Charter School Growth Fund portfolio.',
  [TAB_UPLOADS]: 'Users tied to schools that have student addresses, user-edited enrollment, or grade caps and targets.',
};

const AllUsersUsage = ({ month, onMonthChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const monthOptions = useMemo(buildMonthOptions, []);

  const [excludeAdmins, setExcludeAdmins] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productUsageService.getSummary({ month, excludeAdmins });
      setReport(data);
    } catch (error) {
      console.error('Product usage error:', error);
      enqueueSnackbar(error.message || 'Failed to load product usage', { variant: 'error' });
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [month, excludeAdmins, enqueueSnackbar]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const download = useCallback(async () => {
    setDownloading(true);
    try {
      await productUsageService.downloadSummary({ month, excludeAdmins });
    } catch (error) {
      console.error('Product usage download error:', error);
      enqueueSnackbar(error.message || 'Download failed', { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  }, [month, excludeAdmins, enqueueSnackbar]);

  const period = report?.period;

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Select
            size="small"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}{option.inProgress ? ' (in progress)' : ''}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={
              <Switch
                checked={excludeAdmins}
                onChange={(e) => setExcludeAdmins(e.target.checked)}
                size="small"
              />
            }
            label="Exclude admin users"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={download}
            disabled={!report || downloading}
            variant="contained"
          >
            Export CSV
          </Button>
        </Stack>

        {period && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Reporting period: ${period.period_start} – ${period.period_end}`} />
            <Chip size="small" label={`Anchor: ${period.anchor_date}`} color="primary" />
            <Chip size="small" label={`Rolling 90 days: ${period.engaged_start} – ${period.anchor_date}`} />
            {report?.usage_tracking?.tracking_start && (
              <Chip size="small" label={`Tracking since: ${report.usage_tracking.tracking_start}`} />
            )}
            {typeof report?.total_users === 'number' && (
              <Chip size="small" variant="outlined" label={`${report.total_users.toLocaleString()} users with any usage`} />
            )}
          </Stack>
        )}
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {report?.usage_tracking?.tracking_start == null && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Product usage tracking has no recorded events yet, so all tool counts are zero.
        </Alert>
      )}
      {report?.usage_tracking?.partial && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Product usage tracking started {report.usage_tracking.tracking_start}, partway
          through this period — Current Month tool counts cover only part of the month.
          Rolling 90 and All Time include every event recorded since tracking began.
        </Alert>
      )}

      {report && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Usage by tool</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Distinct users, user-days, and analyses run. Switch the horizon to compare
            the current month, the rolling 90 days ending on the anchor date, and all
            recorded time.
          </Typography>
          <ProductUsageTable
            productUsage={report.product_usage}
            period={period}
            usageTracking={report.usage_tracking}
            showNetworks={false}
          />
        </Paper>
      )}
    </>
  );
};

const ProductUsage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = requested === TAB_CSGF || requested === TAB_UPLOADS ? requested : TAB_ALL;
  const [month, setMonth] = useState(defaultMonth);
  const [visited, setVisited] = useState(() => ({ [tab]: true }));

  useEffect(() => {
    setVisited((prev) => (prev[tab] ? prev : { ...prev, [tab]: true }));
  }, [tab]);

  const setTab = (_event, next) => {
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      if (next === TAB_ALL) nextParams.delete('tab');
      else nextParams.set('tab', next);
      return nextParams;
    }, { replace: true });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ mb: 2, p: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <InsightsIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Product Usage
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {TAB_COPY[tab]}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={setTab} sx={{ mb: 2 }}>
        <Tab value={TAB_ALL} label="All users" />
        <Tab value={TAB_CSGF} label="CSGF" />
        <Tab value={TAB_UPLOADS} label="Data uploads" />
      </Tabs>

      {visited[TAB_ALL] && (
        <Box hidden={tab !== TAB_ALL}>
          <AllUsersUsage month={month} onMonthChange={setMonth} />
        </Box>
      )}
      {visited[TAB_CSGF] && (
        <Box hidden={tab !== TAB_CSGF}>
          <CSGFReporting month={month} onMonthChange={setMonth} />
        </Box>
      )}
      {visited[TAB_UPLOADS] && (
        <Box hidden={tab !== TAB_UPLOADS}>
          <DataUploads />
        </Box>
      )}
    </Box>
  );
};

export default ProductUsage;
